<template>
  <div>
    <PageHeader :title="t('title')">
      <StoreExportButton />
    </PageHeader>
    <SearchToolbar
      class="mb-6"
      :placeholder="t('search_placeholder')"
      :initial-keywords="keywords"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      @update-keyword-search="updateKeywordSearch"
      @update-filter-search="updateFilterSearch"
    />
    <Loading v-if="loading" />
    <div v-else-if="stores.length === 0" class="text-center mt-4 py-2">
      <p>{{ t("no_results") }}</p>
    </div>
    <div v-else>
      <Table :columns="columns" :data="formattedData" isAccessible @view="goToDetails">
        <template #source="{ row }">
          <div
            v-if="row.source"
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="sourceStyle(row.source)"
          >
            {{ t(`store.source.${row.source}`) }}
          </div>
        </template>
        <template #salesManager="{ row }">
          <div class="flex items-center gap-x-2">
            <span>{{ row.salesManagerName || "—" }}</span>
            <button
              type="button"
              data-test="edit-sales-manager"
              class="text-gray-400 hover:text-gray-600 transition-colors duration-150"
              :aria-label="t('edit_sales_manager')"
              @click.stop="openSalesManagerModal(row)"
            >
              <PencilAltIcon class="h-4 w-4" />
            </button>
          </div>
        </template>
        <template #status="{ row }">
          <FieldSpan
            :style-to-add="statusBadgeClass(statusOrSetup(row))"
            :value="statusOrSetup(row)"
            translation-prefix="store.status_label"
          />
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalCount="totalCount"
        @changePage="handleChangePage"
      />
    </div>

    <StoreSalesManagerModal
      v-if="storeBeingEdited"
      :storeId="storeBeingEdited.id"
      :storeName="storeBeingEdited.name"
      :salesManagerId="storeBeingEdited.salesManagerId"
      @close="storeBeingEdited = null"
      @updated="handleSalesManagerUpdated"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { PencilAltIcon } from "@heroicons/vue/outline";
import { PageHeader, Loading, Table, Pagination, FieldSpan, SearchToolbar } from "@estaly/ui";
import StoreExportButton from "@/components/stores/StoreExportButton.vue";
import StoreSalesManagerModal from "./StoreSalesManagerModal.vue";

export default {
  components: {
    PencilAltIcon,
    PageHeader,
    Loading,
    Table,
    Pagination,
    FieldSpan,
    SearchToolbar,
    StoreExportButton,
    StoreSalesManagerModal,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      loading: true,
      keywords: null,
      availableFilters: { status: [] },
      selectedFilters: { status: ["active"] },
      columns: [],
      storeBeingEdited: null,
    };
  },

  computed: {
    ...mapGetters("auth", ["isInsurerAdmin"]),
    ...mapGetters("stores", ["statuses", "statusOrSetup", "statusBadgeClass"]),

    ...mapState("stores", {
      stores: "index",
      currentPage: "currentPage",
      totalPages: "totalPages",
      totalCount: "totalCount",
    }),

    formattedData() {
      return this.stores.map((store) => ({
        id: store.id,
        name: store.salesChannel || "—",
        businessName: store.businessName,
        organizationId: store.organization?.id,
        organizationName: store.organization?.name,
        source: store.source,
        status: store.status,
        setupStatus: store.setupStatus,
        salesManagerId: store.salesManager?.id || null,
        salesManagerName: store.salesManager
          ? [store.salesManager.firstName, store.salesManager.lastName].filter(Boolean).join(" ")
          : null,
        thirdPartyActors: store.thirdPartyActors?.join(", ") || null,
      }));
    },
  },

  async created() {
    this.availableFilters.status = this.statuses().map((status) => ({
      label: this.t(`store.status_label.${status}`),
      value: status,
    }));
    this.buildColumns();
    await this.retrieveStores();
  },

  methods: {
    ...mapActions("stores", { fetchStores: "fetchIndex" }),

    buildColumns() {
      this.columns = [
        { label: this.t("column_id"), field: "id", widthClass: "w-16" },
        { label: this.t("column_name"), field: "name", bold: true },
        { label: this.t("column_business_name"), field: "businessName" },
        { label: this.t("column_organization"), field: "organizationName" },
        { label: this.t("column_organization_id"), field: "organizationId" },
        { label: this.t("column_sales_channel"), field: "source", slot: "source" },
        { label: this.t("column_status"), field: "status", slot: "status" },
      ];

      if (this.isInsurerAdmin) {
        this.columns.push({ label: this.t("column_sales_manager"), field: "salesManagerName", slot: "salesManager" });
      }

      this.columns.push({ label: this.t("column_third_party_actors"), field: "thirdPartyActors" });
    },

    async retrieveStores(page = 1) {
      this.loading = true;
      let response;
      try {
        response = await this.fetchStores({
          page,
          keywords: this.keywords,
          status: this.selectedFilters.status,
          includes: ["organization", "sales_manager", "third_party_actors"],
          order: "-created_at",
        });
      } catch (error) {
        console.error(error);
      } finally {
        // An outdated response must not stop the loader of the request still pending.
        if (response?.isLatest !== false) this.loading = false;
      }
    },

    handleChangePage(page) {
      this.retrieveStores(page);
    },

    updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.retrieveStores();
    },

    goToDetails(row) {
      this.$router.push(`/stores/${row.id}`);
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.retrieveStores();
    },

    openSalesManagerModal(row) {
      this.storeBeingEdited = row;
    },

    async handleSalesManagerUpdated() {
      this.storeBeingEdited = null;
      await this.retrieveStores(this.currentPage);
    },

    sourceStyle(source) {
      switch (source) {
        case "shopify":
          return "bg-green-100 text-green-800";
        case "offline":
          return "bg-gray-100 text-gray-800";
        case "woo_commerce":
          return "bg-purple-100 text-purple-800";
        case "prestashop":
          return "bg-blue-100 text-blue-800";
        case "magento2":
        case "magento1":
          return "bg-orange-100 text-orange-800";
        case "api":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
  },
};
</script>

<i18n src="./StoreList.i18n.json"></i18n>
